import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { Group } from 'three';
import type { ModelProps, ModelRegistry } from '../types';

// ============================================
// LORD OF THE RINGS MODELS
// ============================================

// Animated Eye of Sauron Component
function EyeOfSauron() {
  const eyeRef = useRef<Group>(null);
  const beamRef = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    if (eyeRef.current) {
      // Slow rotation scanning the area
      eyeRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.3) * Math.PI * 0.4;
    }
    if (beamRef.current) {
      // Pulsating beam intensity
      const material = beamRef.current.material as THREE.MeshBasicMaterial;
      material.opacity = 0.15 + Math.sin(state.clock.elapsedTime * 2) * 0.05;
    }
  });

  return (
    <group ref={eyeRef}>
      {/* Outer flame ring */}
      <mesh rotation={[0, Math.PI / 2, 0]}>
        <torusGeometry args={[0.8, 0.12, 8, 24]} />
        <meshBasicMaterial color="#ff4400" />
      </mesh>
      {/* Middle flame ring */}
      <mesh rotation={[0, Math.PI / 2, 0]}>
        <torusGeometry args={[0.6, 0.1, 8, 20]} />
        <meshBasicMaterial color="#ff6600" />
      </mesh>
      {/* Inner flame ring */}
      <mesh rotation={[0, Math.PI / 2, 0]}>
        <torusGeometry args={[0.4, 0.08, 8, 16]} />
        <meshBasicMaterial color="#ffaa00" />
      </mesh>
      {/* Eye shape - outer orange glow */}
      <mesh position={[0, 0, 0]} scale={[0.3, 1.6, 1]}>
        <sphereGeometry args={[0.5, 16, 12]} />
        <meshBasicMaterial color="#ff4400" />
      </mesh>
      {/* Eye shape - inner yellow */}
      <mesh position={[0, 0, 0]} scale={[0.25, 1.4, 0.9]}>
        <sphereGeometry args={[0.45, 14, 10]} />
        <meshBasicMaterial color="#ff8800" />
      </mesh>
      {/* Eye core - bright */}
      <mesh position={[0, 0, 0]} scale={[0.2, 1.2, 0.8]}>
        <sphereGeometry args={[0.35, 12, 8]} />
        <meshBasicMaterial color="#ffcc00" />
      </mesh>
      {/* Pupil - vertical black slit */}
      <mesh position={[0, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
        <boxGeometry args={[0.5, 1.1, 0.06]} />
        <meshBasicMaterial color="#000000" />
      </mesh>
      {/* Flame tongues around eye */}
      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => {
        const angle = (i / 12) * Math.PI * 2;
        const height = 0.3 + (i % 3) * 0.15;
        return (
          <mesh
            key={i}
            position={[
              0,
              Math.sin(angle) * 0.95,
              Math.cos(angle) * 0.95
            ]}
            rotation={[angle, 0, 0]}
          >
            <coneGeometry args={[0.06, height, 4]} />
            <meshBasicMaterial color={i % 2 === 0 ? "#ff4400" : "#ff6600"} />
          </mesh>
        );
      })}
      {/* Scanning light beam - cone projecting forward */}
      <mesh ref={beamRef} position={[6, 0, 0]} rotation={[0, 0, -Math.PI / 2]}>
        <coneGeometry args={[4, 12, 16, 1, true]} />
        <meshBasicMaterial
          color="#ff2200"
          transparent
          opacity={0.15}
          side={THREE.DoubleSide}
          depthWrite={false}
        />
      </mesh>
      {/* Central glow light */}
      <pointLight position={[0.5, 0, 0]} color="#ff4400" intensity={5} distance={15} decay={2} />
      {/* Spotlight for the beam effect */}
      <spotLight
        position={[0.5, 0, 0]}
        target-position={[15, 0, 0]}
        color="#ff3300"
        intensity={3}
        distance={20}
        angle={0.4}
        penumbra={0.5}
        decay={2}
      />
    </group>
  );
}

// Hobbit Hole Model
export function HobbitHoleModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Hill/mound */}
      <mesh position={[0, 0.6, 0]}>
        <sphereGeometry args={[1.5, 12, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color="#4a7c4e" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Door frame (round) */}
      <mesh position={[0, 0.6, 1.3]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.5, 0.08, 8, 16]} />
        <meshStandardMaterial color="#8b4513" roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Door */}
      <mesh position={[0, 0.6, 1.25]}>
        <circleGeometry args={[0.45, 16]} />
        <meshStandardMaterial color="#228b22" roughness={0.7} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Door knob (center) */}
      <mesh position={[0, 0.6, 1.32]}>
        <sphereGeometry args={[0.05, 8, 8]} />
        <meshStandardMaterial color="#ffd700" metalness={0.8} roughness={0.3} />
      </mesh>
      {/* Windows */}
      <mesh position={[0.8, 0.8, 1.1]}>
        <circleGeometry args={[0.2, 12]} />
        <meshStandardMaterial color="#ffffe0" emissive="#ffcc00" emissiveIntensity={0.2} />
      </mesh>
      <mesh position={[-0.8, 0.8, 1.1]}>
        <circleGeometry args={[0.2, 12]} />
        <meshStandardMaterial color="#ffffe0" emissive="#ffcc00" emissiveIntensity={0.2} />
      </mesh>
      {/* Chimney */}
      <mesh position={[0.5, 1.3, -0.3]}>
        <cylinderGeometry args={[0.1, 0.12, 0.4, 6]} />
        <meshStandardMaterial color="#8b7355" roughness={0.9} />
      </mesh>
      {/* Fence */}
      {[-1.5, -1, -0.5, 0.5, 1, 1.5].map((x, i) => (
        <mesh key={`fence-${i}`} position={[x, 0.2, 1.8]}>
          <boxGeometry args={[0.05, 0.4, 0.05]} />
          <meshStandardMaterial color="#8b7355" roughness={0.9} />
        </mesh>
      ))}
    </group>
  );
}

// The One Ring Model
export function TheOneRingModel({ world }: ModelProps) {
  return (
    <group>
      <mesh position={[0, 0.3, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.15, 0.04, 16, 32]} />
        <meshStandardMaterial
          color="#ffd700"
          metalness={0.95}
          roughness={0.1}
          emissive="#ff6600"
          emissiveIntensity={world === 'spectral' ? 0.5 : 0.1}
        />
      </mesh>
      {/* Glow effect */}
      <pointLight position={[0, 0.3, 0]} color="#ffd700" intensity={0.5} distance={1} />
    </group>
  );
}

// Barad-dûr (Tower of Sauron) Model
export function BaradDurModel({ isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Base massive */}
      <mesh position={[0, 1.5, 0]}>
        <cylinderGeometry args={[1.2, 2, 3, 8]} />
        <meshStandardMaterial color="#1a1a1a" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Second level */}
      <mesh position={[0, 4, 0]}>
        <cylinderGeometry args={[0.9, 1.2, 2, 8]} />
        <meshStandardMaterial color="#151515" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Main tower */}
      <mesh position={[0, 7, 0]}>
        <cylinderGeometry args={[0.5, 0.8, 4, 8]} />
        <meshStandardMaterial color="#0a0a0a" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Eye platform - two prongs */}
      <mesh position={[0.4, 9.5, 0]} rotation={[0, 0, 0.2]}>
        <boxGeometry args={[0.15, 2, 0.15]} />
        <meshStandardMaterial color="#0a0a0a" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[-0.4, 9.5, 0]} rotation={[0, 0, -0.2]}>
        <boxGeometry args={[0.15, 2, 0.15]} />
        <meshStandardMaterial color="#0a0a0a" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Upper spikes */}
      <mesh position={[0.6, 10.8, 0]} rotation={[0, 0, 0.3]}>
        <coneGeometry args={[0.1, 0.8, 4]} />
        <meshStandardMaterial color="#0a0a0a" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[-0.6, 10.8, 0]} rotation={[0, 0, -0.3]}>
        <coneGeometry args={[0.1, 0.8, 4]} />
        <meshStandardMaterial color="#0a0a0a" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Lava base */}
      <mesh position={[0, 0.05, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[3, 16]} />
        <meshStandardMaterial color="#ff4400" emissive="#ff2200" emissiveIntensity={0.5} />
      </mesh>
    </group>
  );
}

// Eye of Sauron Model (uses animated component)
export function EyeOfSauronModel(_props: ModelProps) {
  return <EyeOfSauron />;
}

// Dark Mountain Model
export function DarkMountainModel({ isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Main peak */}
      <mesh position={[0, 2, 0]}>
        <coneGeometry args={[2, 4, 6]} />
        <meshStandardMaterial color="#1a1515" roughness={1} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Secondary peak */}
      <mesh position={[1, 1.5, 0.5]} rotation={[0.1, 0.3, 0.15]}>
        <coneGeometry args={[1.2, 3, 5]} />
        <meshStandardMaterial color="#201818" roughness={1} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Third peak */}
      <mesh position={[-0.8, 1.2, -0.3]} rotation={[-0.1, -0.2, -0.1]}>
        <coneGeometry args={[1, 2.5, 5]} />
        <meshStandardMaterial color="#181515" roughness={1} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Base rocks */}
      <mesh position={[0, 0.3, 0]}>
        <dodecahedronGeometry args={[1.5, 0]} />
        <meshStandardMaterial color="#151212" roughness={1} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Lava cracks glow */}
      <mesh position={[0.5, 0.8, 0.8]} rotation={[0.5, 0.3, 0]}>
        <boxGeometry args={[0.05, 1, 0.02]} />
        <meshBasicMaterial color="#ff3300" />
      </mesh>
      <mesh position={[-0.3, 1.2, 0.6]} rotation={[0.3, -0.2, 0.1]}>
        <boxGeometry args={[0.04, 0.8, 0.02]} />
        <meshBasicMaterial color="#ff4400" />
      </mesh>
    </group>
  );
}

// Mount Doom Model
export function MountDoomModel({ isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Main volcanic cone */}
      <mesh position={[0, 2.5, 0]}>
        <coneGeometry args={[3, 5, 8]} />
        <meshStandardMaterial color="#1a1010" roughness={1} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Crater rim */}
      <mesh position={[0, 4.8, 0]}>
        <torusGeometry args={[0.9, 0.25, 6, 12]} />
        <meshStandardMaterial color="#0a0505" roughness={1} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Lava in crater */}
      <mesh position={[0, 4.6, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.8, 12]} />
        <meshBasicMaterial color="#ff4400" />
      </mesh>
      {/* Lava glow */}
      <pointLight position={[0, 5, 0]} color="#ff3300" intensity={4} distance={10} decay={2} />
      {/* Smoke/ash cloud */}
      <mesh position={[0, 6, 0]}>
        <sphereGeometry args={[0.6, 8, 8]} />
        <meshBasicMaterial color="#2a2020" transparent opacity={0.5} />
      </mesh>
      <mesh position={[0.3, 6.8, 0.2]}>
        <sphereGeometry args={[0.4, 6, 6]} />
        <meshBasicMaterial color="#3a3030" transparent opacity={0.4} />
      </mesh>
      {/* Lava flows down the side */}
      <mesh position={[0.8, 2, 1.8]} rotation={[0.7, 0, 0.2]}>
        <boxGeometry args={[0.2, 2.5, 0.08]} />
        <meshBasicMaterial color="#ff3300" />
      </mesh>
      <mesh position={[-1, 1.5, 1.5]} rotation={[0.5, 0, -0.3]}>
        <boxGeometry args={[0.15, 2, 0.06]} />
        <meshBasicMaterial color="#ff4400" />
      </mesh>
      {/* Rocky base */}
      <mesh position={[0, 0.3, 0]}>
        <dodecahedronGeometry args={[2, 0]} />
        <meshStandardMaterial color="#151010" roughness={1} transparent={isOccluding} opacity={opacity} />
      </mesh>
    </group>
  );
}

// Elvish Statue Model
export function ElvishStatueModel({ isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Base */}
      <mesh position={[0, 0.15, 0]}>
        <cylinderGeometry args={[0.4, 0.5, 0.3, 8]} />
        <meshStandardMaterial color="#d4d4d4" roughness={0.4} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Body */}
      <mesh position={[0, 0.8, 0]}>
        <capsuleGeometry args={[0.15, 0.6, 6, 8]} />
        <meshStandardMaterial color="#e8e8e8" roughness={0.3} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Head */}
      <mesh position={[0, 1.25, 0]}>
        <sphereGeometry args={[0.12, 8, 8]} />
        <meshStandardMaterial color="#e8e8e8" roughness={0.3} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Arms raised */}
      <mesh position={[0.2, 1, 0]} rotation={[0, 0, -0.8]}>
        <capsuleGeometry args={[0.05, 0.3, 4, 6]} />
        <meshStandardMaterial color="#e8e8e8" roughness={0.3} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[-0.2, 1, 0]} rotation={[0, 0, 0.8]}>
        <capsuleGeometry args={[0.05, 0.3, 4, 6]} />
        <meshStandardMaterial color="#e8e8e8" roughness={0.3} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Star held above */}
      <mesh position={[0, 1.5, 0]}>
        <octahedronGeometry args={[0.08]} />
        <meshStandardMaterial color="#ffffff" emissive="#aaccff" emissiveIntensity={0.5} />
      </mesh>
      <pointLight position={[0, 1.5, 0]} color="#aaccff" intensity={0.5} distance={2} />
    </group>
  );
}

// Minas Tirith Model
export function MinasTirithModel({ isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Base level */}
      <mesh position={[0, 0.3, 0]}>
        <cylinderGeometry args={[2, 2.5, 0.6, 8]} />
        <meshStandardMaterial color="#f5f5f5" roughness={0.6} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Level 2 */}
      <mesh position={[0, 0.8, 0]}>
        <cylinderGeometry args={[1.6, 2, 0.5, 8]} />
        <meshStandardMaterial color="#f0f0f0" roughness={0.6} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Level 3 */}
      <mesh position={[0, 1.25, 0]}>
        <cylinderGeometry args={[1.2, 1.6, 0.4, 8]} />
        <meshStandardMaterial color="#e8e8e8" roughness={0.6} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Level 4 */}
      <mesh position={[0, 1.6, 0]}>
        <cylinderGeometry args={[0.8, 1.2, 0.35, 8]} />
        <meshStandardMaterial color="#e0e0e0" roughness={0.6} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Citadel */}
      <mesh position={[0, 2.1, 0]}>
        <cylinderGeometry args={[0.3, 0.6, 0.6, 6]} />
        <meshStandardMaterial color="#ffffff" roughness={0.5} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* White tower */}
      <mesh position={[0, 2.8, 0]}>
        <cylinderGeometry args={[0.15, 0.2, 1, 6]} />
        <meshStandardMaterial color="#ffffff" roughness={0.4} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Spire */}
      <mesh position={[0, 3.5, 0]}>
        <coneGeometry args={[0.1, 0.4, 6]} />
        <meshStandardMaterial color="#c0c0c0" metalness={0.5} transparent={isOccluding} opacity={opacity} />
      </mesh>
    </group>
  );
}

// Dead Marshes Model
export function DeadMarshesModel({ isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Water surface */}
      <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[3, 3]} />
        <meshStandardMaterial color="#2a3a2a" transparent opacity={0.8} roughness={0.3} />
      </mesh>
      {/* Ghost faces under water */}
      {[[0.5, -0.1, 0.3], [-0.6, -0.15, -0.4], [0.2, -0.12, -0.7], [-0.3, -0.08, 0.6]].map(([x, y, z], i) => (
        <mesh key={`face-${i}`} position={[x, y, z]}>
          <sphereGeometry args={[0.15, 8, 8]} />
          <meshStandardMaterial
            color="#8090a0"
            transparent
            opacity={isOccluding ? opacity * 0.4 : 0.4}
            emissive="#4060a0"
            emissiveIntensity={0.2}
          />
        </mesh>
      ))}
      {/* Reeds */}
      {[[-0.8, 0.3, 0.8], [0.9, 0.25, -0.6], [-0.5, 0.35, -0.9]].map(([x, y, z], i) => (
        <mesh key={`reed-${i}`} position={[x, y, z]}>
          <cylinderGeometry args={[0.02, 0.01, 0.5, 4]} />
          <meshStandardMaterial color="#4a5a3a" />
        </mesh>
      ))}
      {/* Eerie fog */}
      <mesh position={[0, 0.2, 0]}>
        <sphereGeometry args={[1.5, 8, 8]} />
        <meshBasicMaterial color="#4a5a4a" transparent opacity={0.15} />
      </mesh>
    </group>
  );
}

// ============================================
// MODEL REGISTRY
// ============================================

export const lordOfTheRingsModels: ModelRegistry = {
  hobbit_hole: HobbitHoleModel,
  the_one_ring: TheOneRingModel,
  barad_dur: BaradDurModel,
  eye_of_sauron: EyeOfSauronModel,
  dark_mountain: DarkMountainModel,
  mount_doom: MountDoomModel,
  elvish_statue: ElvishStatueModel,
  minas_tirith: MinasTirithModel,
  dead_marshes: DeadMarshesModel,
};
